import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ActiveWorkoutProvider } from "./contexts/ActiveWorkoutContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import BottomNav from "./components/BottomNav";
import useGlobalHapticOnce from "./hooks/useGlobalHaptic";
import useKeyboardAwareInputs from "./hooks/useKeyboardAwareInputs";
import SignIn from "./screens/SignIn";
import VerifyMagicLink from "./screens/VerifyMagicLink";
import ProfileSetup from "./screens/ProfileSetup";
import Today from "./screens/Today";
import Session from "./screens/Session";
import Crew from "./screens/Crew";
import Profile from "./screens/Profile";
import History from "./screens/History";
import Exercises from "./screens/Exercises";
import RoutineEditor from "./screens/RoutineEditor";
import FinishSummary from "./screens/FinishSummary";
import Compare from "./screens/Compare";
import Routines from "./screens/Routines";
import Settings from "./screens/Settings";
import WallpaperLayer from "./components/WallpaperLayer";

/** Page-transition wrapper — a quick slide-up on route change so
 *  the whole screen doesn't just snap between colours. */
function PageTransition({ children }) {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-transition">
      {children}
    </div>
  );
}

/** Every tab switch should start the user at the top of the new screen.
 *  Without this, the browser preserves the previous page's scroll offset
 *  which makes some tabs (like Profile) appear opened halfway down. */
function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Some scrollable containers inside the mobile-container also need
    // their scrollTop reset — target both the outer wrapper and any inner
    // mobile-container element.
    document.querySelectorAll('.content-wrapper, .mobile-container').forEach((el) => {
      el.scrollTop = 0;
    });
  }, [pathname]);
  return null;
}

/** Nav bar rendered at the App root so it stays mounted across route
 *  changes — the glass puck can then animate smoothly between tabs.
 *  Hidden on auth/setup screens. */
function PersistentNav() {
  const location = useLocation();
  const { user } = useAuth();
  const HIDE_ON = ['/signin', '/auth/verify', '/setup'];
  if (!user || HIDE_ON.some((p) => location.pathname.startsWith(p))) return null;
  return <BottomNav />;
}

function App() {
  useGlobalHapticOnce();
  useKeyboardAwareInputs();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <ActiveWorkoutProvider>
              <WallpaperLayer />
              <ScrollToTopOnRouteChange />
              <PageTransition>
                <Routes>
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/auth/verify" element={<VerifyMagicLink />} />
                  <Route path="/setup" element={<ProfileSetup />} />

                  <Route path="/" element={<ProtectedRoute><Today /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                  <Route path="/exercises" element={<ProtectedRoute><Exercises /></ProtectedRoute>} />
                  <Route path="/session/:workoutId" element={<ProtectedRoute><Session /></ProtectedRoute>} />
                  <Route path="/crew" element={<ProtectedRoute><Crew /></ProtectedRoute>} />
                  {/* Legacy /progress redirects to the new Profile tab */}
                  <Route path="/progress" element={<Navigate to="/profile" replace />} />
                  <Route path="/routines" element={<ProtectedRoute><Routines /></ProtectedRoute>} />
                  <Route path="/routine/new" element={<ProtectedRoute><RoutineEditor /></ProtectedRoute>} />
                  <Route path="/routine/:routineId/edit" element={<ProtectedRoute><RoutineEditor /></ProtectedRoute>} />
                  <Route path="/compare/:workoutId" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
                  <Route path="/finish-summary" element={<ProtectedRoute><FinishSummary /></ProtectedRoute>} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PageTransition>
              <PersistentNav />
            </ActiveWorkoutProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
